import { useState } from 'react';
import { toast } from 'sonner';
import { ShieldCheck, Loader2, AlertTriangle, Send } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { BotAvatar } from '@/components/BotAvatar';

import { ASSET_FALLBACKS } from '@/constants/federation';

const RING_COUNCIL = ASSET_FALLBACKS.ringOfSix;
const RING_SYNTH = ASSET_FALLBACKS.ringOfTwelveSynth;

const PERSPECTIVE_SPECTRAL = { MIND: 'blue', HEART: 'blue', HANDS: 'gold', LEGS: 'blue', EYE: 'purple', AGENT: 'red' };
const COLORS = { MIND: 'text-blue-400', HEART: 'text-rose-400', HANDS: 'text-amber-400', LEGS: 'text-emerald-400', EYE: 'text-purple-400', AGENT: 'text-cyan-400' };
const VERIFICATION_SITE = "https://nextxus.org";

export default function TruthVerifier({ apiUrl }) {
  const [question, setQuestion] = useState('');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [mode, setMode] = useState('ring');

  const runVerification = async () => {
    if (!question.trim()) return;
    setLoading(true);
    setResult(null);
    try {
      const endpoint = mode === 'ring' ? 'ring-of-six' : 'hallucination-check';
      const body = mode === 'ring' ? { question } : { claim: question };
      const r = await fetch(`${apiUrl}/hub/${endpoint}`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
      });
      const data = await r.json();
      setResult(data);
    } catch (e) { toast.error('Verification failed'); }
    setLoading(false);
  };

  const perspectives = result?.perspectives || result?.checks || {};
  const hasResults = Object.keys(perspectives).length > 0;

  return (
    <div className="min-h-screen text-slate-200" data-testid="truth-verifier">

      {/* ===== COUNCIL HERO ===== */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img src={RING_COUNCIL} alt="Ring of 12 Council" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-b from-slate-950/80 via-slate-950/70 to-slate-950" />
        </div>
        <div className="relative max-w-3xl mx-auto px-4 pt-14 pb-10 text-center">
          <ShieldCheck className="w-10 h-10 text-cyan-400 mx-auto mb-3" />
          <h1 className="text-3xl sm:text-4xl font-black text-white mb-2" data-testid="verifier-title">
            Ring of Six <span className="text-cyan-400">Truth Verifier</span>
          </h1>
          <p className="text-sm text-slate-400 mb-1">Six perspectives. One truth. Every claim examined from all angles.</p>
          <p className="text-[10px] text-slate-600">HumanCodex NextXus Federation — Roger Keyserling</p>

          {/* Mode Toggle */}
          <div className="flex justify-center gap-3 mt-6 mb-6">
            <Button size="sm" variant={mode === 'ring' ? 'default' : 'outline'} onClick={() => setMode('ring')}
              className={mode === 'ring' ? 'bg-cyan-700 text-white' : 'border-slate-600 text-slate-400'} data-testid="mode-ring">
              Ring of Six (Full)
            </Button>
            <Button size="sm" variant={mode === 'hallucination' ? 'default' : 'outline'} onClick={() => setMode('hallucination')}
              className={mode === 'hallucination' ? 'bg-amber-700 text-white' : 'border-slate-600 text-slate-400'} data-testid="mode-hallucination">
              Hallucination Check (Fast)
            </Button>
          </div>

          {/* Input */}
          <div className="text-left">
            <textarea
              value={question}
              onChange={e => setQuestion(e.target.value)}
              placeholder={mode === 'ring' ? 'Enter a question, claim, or decision to verify...' : 'Enter a claim to check for hallucinations...'}
              className="w-full bg-slate-900/80 border border-slate-700/60 rounded-lg p-4 text-sm text-slate-200 placeholder-slate-600 resize-none focus:outline-none focus:border-cyan-500/60 backdrop-blur-sm"
              rows={3}
              data-testid="verifier-input"
            />
            <div className="flex justify-between items-center mt-3">
              <span className="text-[10px] text-slate-600">{question.length}/5000</span>
              <Button onClick={runVerification} disabled={loading || !question.trim()}
                className="bg-cyan-700 hover:bg-cyan-600 text-white gap-2" data-testid="verify-btn">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                {loading ? 'Deliberating...' : 'Submit to Council'}
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* ===== SYNTHESIS RESULTS ===== */}
      {hasResults && (
        <section className="relative overflow-hidden">
          <div className="absolute inset-0">
            <img src={RING_SYNTH} alt="Ring of 12 Synthesis" className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-slate-950/88" />
          </div>
          <div className="relative max-w-5xl mx-auto px-4 py-10">
            <h2 className="text-lg font-bold text-white mb-5 text-center">Council Deliberation Results</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8" data-testid="verifier-results">
              {Object.entries(perspectives).map(([name, text]) => {
                const spectral = PERSPECTIVE_SPECTRAL[name] || 'blue';
                const color = COLORS[name] || 'text-slate-400';
                return (
                  <Card key={name} className="border border-slate-700/40 bg-slate-900/60 backdrop-blur-sm">
                    <CardContent className="p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <BotAvatar spectral={spectral} size="xs" />
                        <span className={`text-sm font-semibold ${color}`}>{name}</span>
                      </div>
                      <p className="text-xs text-slate-300 leading-relaxed whitespace-pre-wrap">{text}</p>
                    </CardContent>
                  </Card>
                );
              })}
            </div>

            {/* Consensus */}
            {result?.consensus && (
              <Card className="border border-cyan-700/30 bg-cyan-900/10 backdrop-blur-sm mb-4" data-testid="consensus-card">
                <CardContent className="p-4 text-center">
                  <div className="text-[10px] uppercase tracking-wider text-cyan-500 mb-1">Council Consensus</div>
                  <p className="text-sm text-slate-200">{result.consensus}</p>
                  {result.confidence && (
                    <div className="mt-2 text-xs text-cyan-400 font-medium">Confidence: {result.confidence}</div>
                  )}
                </CardContent>
              </Card>
            )}
          </div>
        </section>
      )}

      {/* ===== EMPTY STATE / GUIDANCE ===== */}
      {!hasResults && !loading && (
        <section className="max-w-3xl mx-auto px-4 py-10">
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {[
              { label: 'MIND', desc: 'Logical analysis', spectral: 'blue' },
              { label: 'HEART', desc: 'Emotional resonance', spectral: 'blue' },
              { label: 'HANDS', desc: 'Practical feasibility', spectral: 'gold' },
              { label: 'LEGS', desc: 'Momentum & direction', spectral: 'blue' },
              { label: 'EYE', desc: 'Pattern recognition', spectral: 'purple' },
              { label: 'AGENT', desc: 'Zero verification', spectral: 'red' },
            ].map(p => (
              <div key={p.label} className="bg-slate-900/50 border border-slate-800/40 rounded-lg p-3 flex items-center gap-3">
                <BotAvatar spectral={p.spectral} size="sm" />
                <div>
                  <div className={`text-xs font-semibold ${COLORS[p.label] || 'text-slate-400'}`}>{p.label}</div>
                  <div className="text-[10px] text-slate-600">{p.desc}</div>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* ===== FLAGSHIP LINK ===== */}
      <section className="max-w-3xl mx-auto px-4 pb-10">
        <Card className="border border-amber-700/20 bg-amber-900/5">
          <CardContent className="p-4 flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-500 shrink-0 mt-0.5" />
            <div>
              <p className="text-sm text-amber-300 font-medium">Need deeper verification?</p>
              <p className="text-xs text-slate-400 mt-1">For Ring of 12, forensic fact-checking, news/media verification — use the flagship:</p>
              <a href={VERIFICATION_SITE} target="_blank" rel="noopener noreferrer"
                className="text-xs text-cyan-400 hover:text-cyan-300 mt-1 inline-block" data-testid="flagship-link">
                {VERIFICATION_SITE} — Full Verification Engine
              </a>
            </div>
          </CardContent>
        </Card>
      </section>
    </div>
  );
}
